import { KeyboardEvent, useId, useMemo } from "react";
import Hint from "../Hint/Hint";
import { InfoIcon, SearchIcon, TickIcon } from "../../assets/icons";
import clsx from "clsx";

const NON_LATIN_REGEX = /^[-@./#&+\w\s]*$/;

export enum InputFieldVariant {
  normal = "normal",
  slim = "slim",
}

type Props = {
  label?: string;
  optional?: boolean;
  error?: string;
  tooltip?: string;
  tick?: boolean;
  search?: boolean;
  className?: string;
  inputClassName?: string;
  inputProps?: React.InputHTMLAttributes<HTMLInputElement>;
  hideErrorMessage?: boolean;
  hint?: string;
  prepend?: React.ReactNode;
  append?: React.ReactNode;
  variant?: InputFieldVariant;
  readOnly?: boolean;
  prependMarginTop?: boolean;
  prependPaddingTop?: string;
};

const InputField = ({
  label,
  error,
  optional,
  tooltip,
  tick,
  search,
  className,
  inputClassName,
  inputProps = {},
  hideErrorMessage = false,
  hint,
  prepend,
  append,
  variant = InputFieldVariant.normal,
  readOnly = false,
  prependMarginTop = false,
  prependPaddingTop,
}: Props) => {
  const id = useId();

  const rawValue = inputProps?.value;
  let showDirham = false;
  let displayValue = rawValue;

  if (typeof rawValue === "string" && /^AED\s*/i.test(rawValue)) {
    showDirham = true;
    displayValue = rawValue.replace(/^AED\s*/i, "");
  }

  const effectivePrepend =
    prepend !== undefined ? (
      typeof prepend === "string" && /^AED\s*$/i.test(prepend.trim()) ? (
        <span
          className={clsx("cx-flex cx-items-center cx-relative", {
            "cx-mt-4 cx--mr-2": prependMarginTop,
          })}
        >
          <div className="cx-font-aed-font"> D </div>
        </span>
      ) : (
        <>
          {prependMarginTop ? (
            <span className="cx-flex cx-items-center cx-relative cx-mt-4 cx--mr-2">
              {prepend}
            </span>
          ) : (
            prepend
          )}
        </>
      )
    ) : showDirham ? (
      <span
        className={clsx("cx-flex cx-items-center cx-relative cx-top-[6px] ", {
          "cx-mt-4 cx--mr-2": prependMarginTop,
        })}
      >
        <div className="cx-font-aed-font"> D </div>
      </span>
    ) : undefined;

  const inputStyle = useMemo(() => {
    return clsx({
      "cx-placeholder-transparent cx-peer cx-outline-none cx-w-full": label,
      "cx-rounded-base cx-p-4 focus:cx-pb-2 focus:cx-pt-6 [&:not(:placeholder-shown)]:cx-pt-6 [&:not(:placeholder-shown)]:cx-pb-2":
        label && variant === InputFieldVariant.normal,
      "cx-rounded-[100px] cx-py-1.5 cx-pl-3":
        label && variant !== InputFieldVariant.normal,
      "cx-bg-gray-50 cx-cursor-not-allowed": readOnly,
    });
  }, [label, variant, readOnly]);

  const InputIcon = useMemo(() => {
    if (tick) {
      return <TickIcon height="100%" className="cx-stroke-success-regular" />;
    }
    if (tooltip) {
      return (
        <InfoIcon height="100%" className="cx-stroke-brand-primary-regular" />
      );
    }
    if (optional) {
      return <div className="cx-text-text-tertiary">Optional</div>;
    }
    if (search) {
      return <SearchIcon height="100%" className="cx-stroke-text-tertiary" />;
    }
    return null;
  }, [tick, tooltip, optional]);

  const handleKeyDown = (event: KeyboardEvent) => {
    if (readOnly) {
      event.preventDefault();
      return false;
    }

    if (NON_LATIN_REGEX.test(event.key)) {
      return true;
    }
    event.preventDefault();
    return false;
  };

  return (
    <>
      <div
        className={clsx(
          "cx-flex cx-flex-row cx-relative cx-w-full cx-overflow-hidden cx-bg-background-default cx-peer",
          {
            "cx-rounded-base cx-border-2": variant === InputFieldVariant.normal,
            "cx-rounded-[100px] cx-border-2":
              variant !== InputFieldVariant.normal,
            "cx-border-stroke-error": error,
            "cx-border-stroke-primary": !error,
            "cx-opacity-50": inputProps.disabled,
            "cx-bg-gray-50": readOnly,
          },
          className
        )}
      >
        {effectivePrepend && (
          <div
            className={`cx-pl-4 cx-flex cx-items-center cx-font-normal cx-text-text-secondary ${
              prependPaddingTop ? `${prependPaddingTop}` : ""
            }`}
          >
            {effectivePrepend}
          </div>
        )}
        <input
          id={id}
          className={clsx(
            "cx-bg-none cx-peer cx-outline-none cx-w-full cx-p-4 placeholder:cx-text-sm",
            {
              "cx-rounded-base": variant === InputFieldVariant.normal,
              "cx-rounded-[100px]": variant === InputFieldVariant.slim,
              "cx-bg-gray-50 cx-cursor-not-allowed": readOnly,
            },
            inputStyle,
            inputClassName
          )}
          placeholder={label}
          onKeyDown={handleKeyDown}
          {...inputProps}
          value={displayValue}
          readOnly={readOnly}
        ></input>
        {label && (
          <label
            htmlFor={id}
            className={clsx(
              "cx-absolute cx-text-xs peer-placeholder-shown:cx-text-label cx-transition-all",
              {
                "cx-px-4 cx-pt-4 cx-pb-2 cx-left-0 -cx-top-2 peer-placeholder-shown:cx-p-4 peer-placeholder-shown:cx-text-label peer-placeholder-shown:cx-top-0 peer-focus:-cx-top-2 peer-focus:cx-text-xs":
                  variant === InputFieldVariant.normal,
                "cx-left-1 -cx-top-0 peer-placeholder-shown:cx-py-1.5 peer-placeholder-shown:cx-px-4 peer-focus:cx-hidden":
                  variant === InputFieldVariant.slim,
                "cx-hidden":
                  variant === InputFieldVariant.slim && inputProps?.value,
                "cx-text-text-error": error,
                "cx-text-text-tertiary": !error,
                "cx-text-gray-500": readOnly,
              }
            )}
          >
            {label}
          </label>
        )}
        {InputIcon && (
          <div
            className={clsx({
              "cx-py-4 cx-pr-4": variant === InputFieldVariant.normal,
              "cx-py-1.5 cx-px-3": variant === InputFieldVariant.slim,
            })}
          >
            {InputIcon}
          </div>
        )}
        {append && (
          <div className="cx-pr-4 cx-flex cx-items-center cx-font-normal cx-text-text-secondary">
            {append}
          </div>
        )}
      </div>
      {error && !hideErrorMessage && (
        <div className="cx-text-xs cx-py-1 cx-text-text-error">{error}</div>
      )}
      {!!hint && (
        <div className="cx-max-h-0 cx-transition-[max-height,opacity] cx-ease-in-out cx-opacity-0 cx-duration-700 cx-overflow-hidden peer-focus-within:cx-overflow-visible peer-focus-within:cx-max-h-52 peer-focus-within:cx-opacity-100">
          <Hint text={hint} className="cx-mt-2" icon rounded />
        </div>
      )}
    </>
  );
};

export default InputField;
